import React, { useState, useEffect } from 'react';
import { ApiKey } from '../../types/apiKey';
import { getApiKeys, revokeApiKey } from '../../services/apiKeyService';
import { useToast } from '../../hooks/use-toast';
import { CreateApiKeyDialog } from './CreateApiKeyDialog';
import { ApiKeysList } from './ApiKeysList';
import { Loader2, Key, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';

export const ApiKeysSection: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const loadApiKeys = async () => {
    try {
      setLoading(true);
      setError(null);
      const keys = await getApiKeys();
      setApiKeys(keys);
    } catch (error) {
      console.error('Error loading API keys:', error);
      setError(error instanceof Error ? error.message : 'Failed to load API keys');
      toast({
        title: 'Error',
        description: 'Failed to load API keys',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApiKeys();
  }, []);

  const handleRevoke = async (id: string) => {
    try {
      await revokeApiKey(id);
      toast({
        title: 'API Key Revoked',
        description: 'The API key has been revoked successfully',
      });
      await loadApiKeys();
    } catch (error) {
      console.error('Error revoking API key:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to revoke API key',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="dashboard-card p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-xl font-semibold mb-2 flex items-center space-x-2">
              <Key className="w-5 h-5" />
              <span>API Keys</span>
            </h3>
            <p className="text-muted-foreground text-sm">
              Manage API keys for programmatic access to your workspace. Keep your keys secure and never share them publicly.
            </p>
          </div>
          <CreateApiKeyDialog
            onSuccess={loadApiKeys}
            trigger={
              <Button>
                <Key className="w-4 h-4 mr-2" />
                Create API Key
              </Button>
            }
          />
        </div>

        {/* Info Banner */}
        <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800 dark:text-blue-300">
              <p className="font-medium mb-1">Security Best Practices</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Store API keys securely and never commit them to version control</li>
                <li>Use environment variables to manage keys in your applications</li>
                <li>Rotate keys regularly and revoke unused keys immediately</li>
                <li>Set appropriate expiration dates based on your security requirements</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* API Keys List */}
      <div className="dashboard-card p-6">
        <h4 className="font-semibold mb-4">Your API Keys</h4>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
            <span className="ml-2 text-muted-foreground">Loading API keys...</span>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive opacity-50" />
            <p className="text-destructive mb-4">{error}</p>
            <Button onClick={loadApiKeys} variant="outline">
              Try Again
            </Button>
          </div>
        ) : (
          <ApiKeysList
            apiKeys={apiKeys}
            onRevoke={handleRevoke}
            loading={loading}
          />
        )}
      </div>
    </div>
  );
};
